// Filter options and defaults shared by the search overlay and the status line.

const option = (value) => ({ value, checked: false });

export const RANGE_LIMITS = {
    released: { min: 1950, max: new Date().getFullYear(), step: 1 },
    // Films under an hour are excluded everywhere (views, API validation)
    runtime: { min: 60, max: 240, step: 1 },
    imdb: { min: 0, max: 10, step: 0.1 },
    rottentomatoes: { min: 0, max: 100, step: 1 },
};

export const defaultFilters = () => ({
    streams: ['GooglePlay', 'YouTube', 'iTunes'].map(option),
    // Every original language in the catalogue, most common first
    languages: [
        'English', 'French', 'Spanish', 'German', 'Italian', 'Japanese', 'Chinese', 'Portuguese',
        'Korean', 'Arabic', 'Swedish', 'Persian', 'Russian', 'Danish', 'Dutch', 'Hebrew', 'Polish',
        'Turkish', 'Czech', 'Romanian', 'Greek', 'Hungarian', 'Thai', 'Serbian', 'Finnish',
        'Ukrainian', 'Icelandic', 'Indonesian', 'Georgian', 'Slovak', 'Croatian', 'Bulgarian',
        'Bosnian', 'Kazakh', 'Estonian', 'Latvian', 'Mongolian', 'Slovene', 'Macedonian', 'Kyrgyz',
        'Lithuanian', 'Azerbaijani', 'Armenian', 'Norwegian Bokmål', 'Uzbek',
    ].map(option),
    // Every genre in the catalogue, most common first
    genres: [
        'Drama', 'Comedy', 'Documentary', 'Romance', 'Thriller', 'Crime', 'Adventure', 'History',
        'Action', 'Music', 'Horror', 'Fantasy', 'Animation', 'Mystery', 'Science Fiction', 'War',
        'Family', 'Western',
    ].map(option),
    released: { min: '2015', max: String(new Date().getFullYear()) },
    runtime: { min: '75', max: '240' },
    imdb: { min: '6.5', max: '10' },
    rottentomatoes: { min: '65', max: '100' },
});

const DEFAULTS = defaultFilters();

export const rangeChanged = (filters, id) =>
    String(filters[id].min) !== DEFAULTS[id].min || String(filters[id].max) !== DEFAULTS[id].max;

const checkedValues = (items) => items.filter((i) => i.checked).map((i) => i.value);

const span = (min, max, limitMax, suffix = '') =>
    Number(max) >= limitMax ? `${min}+${suffix}` : `${min}–${max}${suffix}`;

const yearSpan = (from, to) => {
    if (from && to) return `${from}–${to}`;
    if (from) return `${from}+`;
    return `before ${Number(to) + 1}`;
};

// Text for the main-screen status line, e.g. ["“a film with emma stone”", "Romance", "Cannes"].
// Ranges only appear when they differ from the defaults. While a search is active, genres,
// languages and years come from what the search actually applied (Claude may have set them).
export const describeActiveFilters = (filters, searchQuery, searchIntent) => {
    const parts = [];
    if (searchQuery) parts.push(`“${searchQuery}”`);

    if (searchQuery && searchIntent) {
        (searchIntent.unmatched || []).forEach((name) => parts.push(`no match for ${name}`));
        (searchIntent.people || []).forEach(({ name, role }) => {
            if (role === 'director') parts.push(`directed by ${name}`);
            else if (role === 'writer') parts.push(`written by ${name}`);
            else parts.push(name);
        });
        parts.push(...(searchIntent.companies || []));
        parts.push(...(searchIntent.genres || []), ...(searchIntent.language || []));
        if (searchIntent.from_year || searchIntent.to_year) {
            parts.push(yearSpan(searchIntent.from_year, searchIntent.to_year));
        }
    } else {
        parts.push(...checkedValues(filters.genres), ...checkedValues(filters.languages));
        if (rangeChanged(filters, 'released')) parts.push(`${filters.released.min}–${filters.released.max}`);
    }

    if (rangeChanged(filters, 'runtime')) parts.push(`${filters.runtime.min}–${filters.runtime.max} min`);
    if (rangeChanged(filters, 'imdb')) parts.push('IMDb ' + span(filters.imdb.min, filters.imdb.max, 10));
    if (rangeChanged(filters, 'rottentomatoes')) parts.push('RT ' + span(filters.rottentomatoes.min, filters.rottentomatoes.max, 100));
    return parts;
};
